import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { PromiseResult } from '../models/PromiseResult';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { RestApi } from '../shared/apiUrls';
import { catchError } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { PackageConsultDoctorApiData, SlotsFetchApiData, consultApiData, consultation_status } from '../models/consultdoctors';
import { FinalTransaction } from '../models/transactions';
import { getPatientQuery } from '../models/PatientAccount';
import { patientRefundBody } from '../models/Accounts';
import { PaginationModel } from '../models/util.models';
import { create_health_passport_body } from '../models/patient-profile';

@Injectable({
    providedIn: 'root'
})
export class PatientService {
    constructor(private readonly http: HttpClient) { }

    private handleError(error: HttpErrorResponse) {
        return throwError(error);
    }

    createUser(patient: FormData): Observable<PromiseResult> {
        return this.http
            .post<PromiseResult>(RestApi.Urls.PatientApi + 'Create', patient)
            .pipe(catchError(this.handleError));
    }

    createUserFree(patient: FormData): Observable<PromiseResult> {
        return this.http
            .post<PromiseResult>(
                RestApi.Urls.PatientApi + 'Create/free',
                patient
            )
            .pipe(catchError(this.handleError));
    }

    getMember(user_guid: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'Members/GetAll/' + user_guid
            )
            .pipe(catchError(this.handleError));
    }

    getTransactionStatus(transaction_id: string): Observable<PromiseResult> {
        return this.http.get<PromiseResult>(RestApi.Urls.PatientApi + 'transaction/check/status' + transaction_id)
            .pipe(catchError(this.handleError));
    }
    
    //link/xyz is prams
    //link/?query=xyz is query
    getTransactions(patient_guid: string, query: PaginationModel): Observable<PromiseResult> {
        const params = new HttpParams()
            .set('page_no', query.page_no.toString())
            .set('records_per_page', query.records_per_page.toString());
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'transactions/' + patient_guid + '/get',
                { params }
            )
            .pipe(catchError(this.handleError));
    }

    getPriorityByGuid(
        patient_guid: string,
        consultation_type_id?: number,
        specialty_id?: number,
        package_id?: string
    ): Observable<PromiseResult> {
        let params = new HttpParams();
        params = params
            .append('patient_guid', patient_guid);
        if (specialty_id != null)
            params = params.append('specialty_id', specialty_id.toString());
        if (consultation_type_id != null)
            params = params.append('consultation_type_id', consultation_type_id.toString());
        if (package_id != null)
            params = params.append('package_id', package_id);
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'PriorityList/GetAll',
                { params: params }
            )
            .pipe(catchError(this.handleError));
    }

    CreateMedicalCondtions(
        patient_guid: string,
        current_medical_problems: string
    ): Observable<PromiseResult> {
        return this.http
            .post<PromiseResult>(
                RestApi.Urls.PatientApi + 'Conditions/Create',
                {
                    patient_guid: patient_guid,
                    current_medical_problems: current_medical_problems
                }
            )
            .pipe(catchError(this.handleError));
    }

    updateMedicalCondtions(
        patient_guid: string,
        current_medical_problems: string
    ): Observable<PromiseResult> {
        return this.http
            .put<PromiseResult>(
                RestApi.Urls.PatientApi + 'Conditions/Update/' + patient_guid,
                { current_medical_problems: current_medical_problems }
            )
            .pipe(catchError(this.handleError));
    }

    GetMedicalCondtions(patient_guid: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'Conditions/Get/' + patient_guid
            )
            .pipe(catchError(this.handleError));
    }

    updateMedicalHistory(data): Observable<PromiseResult> {
        return this.http
            .post<PromiseResult>(
                RestApi.Urls.PatientApi + 'MedicalHistory',
                data
            )
            .pipe(catchError(this.handleError));
    }

    updateMedications_Allergies(
        patient_guid: string,
        data
    ): Observable<PromiseResult> {
        return this.http
            .put<PromiseResult>(
                RestApi.Urls.PatientApi + 'AllergiesMedications/Update',
                {
                    patient_guid: patient_guid,
                    allergies: data.allergies,
                    medications: data.medications
                }
            )
            .pipe(catchError(this.handleError));
    }

    GetMedicalHistory(patient_guid: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'MedicalHistory/Get/' + patient_guid
            )
            .pipe(catchError(this.handleError));
    }

    GetMedicationsAllergies(patient_guid: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi +
                'AllergiesMedications/Get/' +
                patient_guid
            )
            .pipe(catchError(this.handleError));
    }

    consultDoctor(consultation: consultApiData): Observable<PromiseResult> {
        return this.http
            .post<PromiseResult>(
                RestApi.Urls.PatientApi + 'Consultation/Create',
                consultation
            )
            .pipe(catchError(this.handleError));
    }
    UploadFiles(data: FormData): Observable<PromiseResult> {
        return this.http
            .post<PromiseResult>(
                RestApi.Urls.PatientApi + 'Document/Upload',
                data
            )
            .pipe(catchError(this.handleError));
    }

    DeleteFiles(data): Observable<any> {
        return this.http
            .post<PromiseResult>(
                RestApi.Urls.PatientApi + 'Document/Delete',
                data
            )
            .pipe(catchError(this.handleError));
    }

    GetDocuments(patient_guid: string, query?: Record<string, string | number | boolean>): Observable<PromiseResult> {
        let params = new HttpParams()
        Object.entries(query).forEach(([key, value]) => (params = params.append(key, value.toString())))
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'Document/Get/' + patient_guid, { params }
            )
            .pipe(catchError(this.handleError));
    }

    getMyRequest(patient_guid: string, body?: Record<string, string | string[] | undefined>): Observable<PromiseResult> {
        return this.http
            .post<PromiseResult>(
                RestApi.Urls.PatientApi + 'consultation/fetch/' + patient_guid, body
            )
            .pipe(catchError(this.handleError));
    }

    getHealthPassportStorage(patient_guid: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'health_passport/get/' + patient_guid
            )
            .pipe(catchError(this.handleError));
    }


    getHealthPassportPriority(patient_guid: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'health_passport/priority/' + patient_guid
            )
            .pipe(catchError(this.handleError));
    }

    createHealthPassport(
        create_passport: create_health_passport_body
    ): Observable<PromiseResult> {
        return this.http
            .post<PromiseResult>(
                RestApi.Urls.PatientApi + 'health_passport/create',
                create_passport
            )
            .pipe(catchError(this.handleError));
    }

    GetHealthSummary(patient_guid: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'HealthSummary/Get/' + patient_guid
            )
            .pipe(catchError(this.handleError));
    }

    GetMyMembershipDetailsData(user_guid: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'Members/Get/' + user_guid + '/Self'
            )
            .pipe(catchError(this.handleError));
    }

    SubmitPaymentToken(
        transaction: FinalTransaction
    ): Observable<PromiseResult> {
        return this.http
            .post<PromiseResult>(
                RestApi.Urls.AccountsApi + 'Transaction/Create',
                transaction
            )
            .pipe(catchError(this.handleError));
    }

    GetDoctorReportspdf(
        patient_guid: string,
        consultation_id: number,
        report_type: string
    ): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi +
                'Consultation/Get/' +
                patient_guid +
                '/Reports/' +
                consultation_id +
                '/' +
                report_type
            )
            .pipe(catchError(this.handleError));
    }
    GetPharmacyData(
        patient_guid: string,
        consultation_id: number
    ): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi +
                patient_guid +
                '/consultation/' +
                consultation_id +
                '/fetch/pharmacy'
            )
            .pipe(catchError(this.handleError));
    }
    GetDoctorReports(patient_guid: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi +
                'Consultation/Get/' +
                patient_guid +
                '/Reports'
            )
            .pipe(catchError(this.handleError));
    }

    GetBase64FromUrl(api_url: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(api_url)
            .pipe(catchError(this.handleError));
    }


    GetHistoryDefaultList(): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(RestApi.Urls.PatientApi + 'HistoryList/GetAll')
            .pipe(catchError(this.handleError));
    }

    fetchInvoice(transaction_id: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi +
                'Transactions/Invoice/Get/' +
                transaction_id
            )
            .pipe(catchError(this.handleError));
    }

    // Update Profile
    UpdateProfile(data: FormData): Observable<PromiseResult> {
        return this.http
            .put<PromiseResult>(
                RestApi.Urls.PatientApi + 'update',
                data
            )
            .pipe(catchError(this.handleError));
    }

    UpdateProfilePhoto(
        form: FormData,
        user_guid: string
    ): Observable<PromiseResult> {
        return this.http
            .put<PromiseResult>(
                RestApi.Urls.PatientApi + 'Update/ProfilePhoto/' + user_guid,
                form
            )
            .pipe(catchError(this.handleError));
    }

    getClosedConsultation(patient_guid: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi +
                'Consultation/Get/' +
                patient_guid +
                '/Follow_Up'
            )
            .pipe(catchError(this.handleError));
    }

    // message center

    GetDoctors(patient_guid: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi +
                'Consultation/Get/' +
                patient_guid +
                '/doctors'
            )
            .pipe(catchError(this.handleError));
    }

    getPatientAppointments(data: getPatientQuery): Observable<PromiseResult> {
        return this.http
            .post<PromiseResult>(
                RestApi.Urls.PatientApi + 'Consultation/fetch/appointments',
                data
            )
            .pipe(catchError(this.handleError));
    }

    patientRefund(body: patientRefundBody): Observable<PromiseResult> {
        return this.http
            .post<PromiseResult>(
                RestApi.Urls.AccountsApi + 'Transaction/refund',
                body
            )
            .pipe(catchError(this.handleError));
    }

    fetchIDStatus(patient_guid: string): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi +
                patient_guid +
                '/identity_proof/status'
            )
            .pipe(catchError(this.handleError));
    }

    findPackages(data: PackageConsultDoctorApiData): Observable<PromiseResult> {
        return this.http
            .post<PromiseResult>(
                RestApi.Urls.PatientApi +
                'consultation/packages/find',
                data
            )
            .pipe(catchError(this.handleError));
    }

    getConsultationAndCommonHealthIssueData(): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'dashboard/fetch/web'
            )
            .pipe(catchError(this.handleError));
    }

    getFreeDietPriceData(): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'dashboard/fetch/free/diet'
            )
            .pipe(catchError(this.handleError));
    }

    getConsultationAndCommonHealthIssueLandingData(): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'dashboard/fetch/common/health/issues'
            )
            .pipe(catchError(this.handleError));
    }

    getAllDietPlans(): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'diet/plans/find'
            )
            .pipe(catchError(this.handleError));
    }

    getDietPreference(): Observable<PromiseResult> {
        return this.http
            .get<PromiseResult>(
                RestApi.Urls.PatientApi + 'consultation/diet/fetch/types'
            )
            .pipe(catchError(this.handleError));
    }

    getSlotsForConsultation(data: SlotsFetchApiData): Observable<PromiseResult> {
        return this.http.post<PromiseResult>(RestApi.Urls.PatientApi + 'consultation/slots-vendor', data).pipe(
            catchError(this.handleError)
        )
    }


}
